import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ArchiveFileRequest, Benutzer, Payload, PayloadBenutzer, PayloadBoolean, PayloadString } from '@karl/entities';
import { catchError, map, Observable, of, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { RouterService } from '../core/services/router.service';
import { AuthStore } from '../core/store/auth/auth.store';
import { SettingData, UserSettingStore } from '../core/store/user-settings/user-setting.store';
import { PayloadUtil } from '../core/util/payload.util';

@Injectable({
  providedIn: 'root'
})
export class RestService {

  protected secured_endpoint = environment.apiUrl;
  protected httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json'
    }),
    credentials: 'include' as const
  };
  public authStore = inject(AuthStore);
  public userSettingStore = inject(UserSettingStore);
  public routerService = inject(RouterService);

  constructor(protected http: HttpClient) {
  }

  setLoggedInUser(initSettings?: boolean): Observable<Benutzer> {
    return this.getBenutzerData()
      .pipe(
        map(user => {
          if (PayloadUtil.checkPayload(user)) {
            return user.data;
          }
          return undefined;
        }),
        tap((user: Benutzer) => {
          let unreadNotifications = 0;
          if (user && user.benachrichtigungen && user.benachrichtigungen.list) {
            unreadNotifications = user.benachrichtigungen.list.filter(n => !n.gelesen).length;
          }
          if (initSettings) {
            this.loadBenutzerSettings()
              .subscribe();
          }
          this.authStore.updateUser(user, unreadNotifications);
        })
      );
  }

  logoutUser() {
    return this
      .logout()
      .pipe(
        tap(() => {
          this.authStore.clearUser();
        })
      )
      .subscribe(() => this.routerService.navigateLogin());
  }

  logoutTimeout() {
    return this
      .logout()
      .pipe(
        tap(() => {
          this.authStore.clearUser();
        })
      )
      .subscribe(() => this.routerService.navigateLoginWithError('Sie wurden aus Sicherheitsgründen abgemeldet'));
  }

  isLoggedIn(): Observable<PayloadBoolean> {
    return this.get<PayloadBoolean>('/protected/loginstatus');
  }

  private logout(): Observable<any> {
    return this.get('/auth/logout');
  }

  private getBenutzerData(): Observable<PayloadBenutzer> {
    return this.get<PayloadBenutzer>('/protected/benutzerdata');
  }

  private getBenutzerSettings(): Observable<PayloadSettingList> {
    return this
      .get('/protected/benutzersettings');
  }

  private putBenutzerSettings(body: SettingData[]): Observable<PayloadBoolean> {
    return (this
      .put('/protected/savebenutzersettings', body));
  }

  loadBenutzerSettings(): Observable<SettingData[]> {
    return this.getBenutzerSettings()
      .pipe(
        map(settings => {
          if (PayloadUtil.checkPayload(settings)) {
            return settings.data;
          }
          return undefined;
        }),
        tap((settings) => {
          this.userSettingStore.updateSettings(settings);
        }));
  }

  saveBenutzerSettings(body: SettingData[]): Observable<PayloadBoolean> {
    return this.putBenutzerSettings(body)
      .pipe(
        tap(() => {
          this.userSettingStore.updateSettings(body);
        }));
  }

  public uploadFile(body: Blob, filename: string): Observable<PayloadString> {
    const formData: FormData = new FormData();
    formData.append('file', body, filename);
    return this
      .post<PayloadString>('/protected/upload', formData, fileUploadOptions);
  }

  uploadChecklist(schadenNr: string, chlId: string, fileObjIds: string[]): Observable<PayloadBoolean> {
    const body = { fileObjectIds: fileObjIds } as ArchiveFileRequest;
    return this
      .post<PayloadBoolean>('/protected/archive/schaden/' + schadenNr + '/checklist/' + chlId, body);
  }

  public get<T>(url: string, options?: { headers: HttpHeaders, credentials?: RequestCredentials }): Observable<T> {
    if (!options) {
      options = this.httpOptions;
    }
    return this.http.get<T>(this.secured_endpoint + url, options)
      .pipe(catchError(this.handleError<any>(url)));
  }

  public post<T>(url: string, body: any, options?: {
    headers?: HttpHeaders,
    credentials?: RequestCredentials
  }, skipUserUpdate?: boolean): Observable<T> {
    if (!options) {
      options = this.httpOptions;
    }
    return this.http
      .post<T>(this.secured_endpoint + url, body, options)
      .pipe(tap(() => {
        if (!skipUserUpdate) {
          this.setLoggedInUser()
            .subscribe();
        }
      }), catchError(this.handleError<any>(url)));
  }

  public put<T>(url: string, body: any, options?: {
    headers: HttpHeaders,
    credentials?: RequestCredentials
  }, skipUserUpdate?: boolean): Observable<T> {
    if (!options) {
      options = this.httpOptions;
    }
    return this.http
      .put<T>(this.secured_endpoint + url, body, options)
      .pipe(tap(() => {
        if (!skipUserUpdate) {
          this.setLoggedInUser()
            .subscribe();
        }
      }), catchError(this.handleError<any>(url)));
  }

  public patch<T>(url: string, body: any, options?: {
    headers: HttpHeaders,
    credentials?: RequestCredentials
  }, skipUserUpdate?: boolean): Observable<T> {
    if (!options) {
      options = this.httpOptions;
    }
    return this.http
      .patch<T>(this.secured_endpoint + url, body, options)
      .pipe(tap(() => {
        if (!skipUserUpdate) {
          this.setLoggedInUser()
            .subscribe();
        }
      }), catchError(this.handleError<any>(url)));
  }

  public delete<T>(url: string, options?: {
    headers: HttpHeaders,
    credentials?: RequestCredentials
  }, clearUser?: boolean): Observable<T> {
    if (!options) {
      options = this.httpOptions;
    }
    return this.http
      .delete<T>(this.secured_endpoint + url, options)
      .pipe(tap(() => {
        if (clearUser) {
          this.authStore.clearUser();
        } else {
          this.setLoggedInUser()
            .subscribe();
        }
      }), catchError(this.handleError<any>(url)));
  }

  private handleError<T>(operation: string = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      // TODO: send the error to remote logging infrastructure
      if (environment.env !== 'prod') {
        console.error(error); // log to console instead
      }
      if (error.status === 401) { // Auto logout after session expired
        if (this.authStore.getCurrentUser()) {
          this.logoutTimeout();
        } else {
          this.routerService.navigateLogin();
        }
      }
      return of(<any>false);
    };
  }

  getCacheFileBlob(docRef: string): Observable<Blob> {
    return this.http.get(this.secured_endpoint + '/file/download/cache/' + docRef, {
      ...this.httpOptions,
      responseType: 'blob'
    });
  }

  getDownloadFileUrl(docRef: number): string {
    return this.secured_endpoint + '/file/download/archive/' + docRef;
  }
}

const fileUploadOptions = {
  credentials: 'include' as const
};

export declare class PayloadSettingList extends Payload {
  data?: SettingData[];
}
